import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { STATUS_COLORS } from '../../config/constants';

const DEFAULT_COLOR = '#9AA0A6';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0];
    return (
      <Paper
        sx={{
          p: 1.5,
          borderRadius: 1,
          border: '1px solid #DADCE0',
          boxShadow: '0 1px 3px rgba(60,64,67,0.3)',
        }}
        elevation={0}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#202124' }}>
          {name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#5F6368' }}>
          Count: {value}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const renderLegend = (props) => {
  const { payload } = props;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 1 }}>
      {payload.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '2px',
              backgroundColor: entry.color,
            }}
          />
          <Typography variant="caption" sx={{ color: '#5F6368', fontSize: '0.7rem' }}>
            {entry.value} ({entry.payload.count})
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const LeadsByStatusChart = ({ data = [] }) => {
  const safeData = Array.isArray(data) ? data : [];
  const chartData = safeData.map((item) => ({
    name: item._id,
    value: item.count,
    count: item.count,
  }));

  if (chartData.length === 0) {
    return (
      <Paper
        sx={{
          p: 3,
          height: '100%',
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.08)',
          },
        }}
        elevation={0}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#202124', fontSize: '1rem' }} gutterBottom>
          Leads by Status
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 300,
          }}
        >
          <Typography variant="body2" sx={{ color: '#5F6368' }}>
            No data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid #DADCE0',
      }}
      elevation={0}
    >
      <Typography variant="h6" sx={{ fontWeight: 500, color: '#202124', fontSize: '1rem' }} gutterBottom>
        Leads by Status
      </Typography>
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="45%"
            innerRadius={60}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
            stroke="none"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={STATUS_COLORS[entry.name] || DEFAULT_COLOR}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={renderLegend} />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default LeadsByStatusChart;
